import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
  CheckCircle2, Circle, Paperclip, Upload, Trash2, Loader2,
  AlertCircle, FileText, ChevronDown, ChevronUp, Clock, User
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const ACCION_LABELS = {
  conformidad_activada: 'Conformidad activada',
  conformidad_desactivada: 'Conformidad desactivada',
  adjunto_subido: 'Adjunto subido',
  adjunto_eliminado: 'Adjunto eliminado',
};

const ACCION_COLORS = {
  conformidad_activada: 'text-green-600',
  conformidad_desactivada: 'text-amber-600',
  adjunto_subido: 'text-blue-600',
  adjunto_eliminado: 'text-red-600',
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
};

const ChecklistPreEntrega = ({ entregaId, fase, tieneArquitecto = false, onGenerado }) => {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [uploading, setUploading] = useState({});
  const [toggling, setToggling] = useState({});
  const fileInputRefs = useRef({});

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const FASES_EDITABLES = ['Inspección', 'Programación', 'Entrega'];
  const editable = FASES_EDITABLES.includes(fase);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/entregas/${entregaId}/checklist-preentrega`);
      setItems(resp.data || []);
    } catch (err) {
      if (err?.response?.status !== 404) toast.error('Error cargando checklist');
    } finally {
      setLoading(false);
    }
  }, [entregaId]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const resp = await api.get(`/entregas/${entregaId}/checklist-preentrega/audit`);
      setAuditLogs(resp.data || []);
    } catch {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  }, [entregaId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  useEffect(() => {
    if (auditOpen) loadAudit();
  }, [auditOpen, loadAudit]);

  const canToggle = (item) => {
    if (!editable) return false;
    if (!item.responsable_id) return true;
    return item.responsable_id === currentUser?.id;
  };

  const handleToggle = async (item) => {
    if (!canToggle(item)) return;
    setToggling(prev => ({ ...prev, [item.id]: true }));
    try {
      const resp = await api.put(`/entregas/checklist-preentrega/${item.id}/conformidad`, {
        es_conforme: !item.es_conforme
      });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, ...resp.data } : i));
      if (auditOpen) loadAudit();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Error actualizando conformidad');
    } finally {
      setToggling(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const handleUpload = async (itemId, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [itemId]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await api.post(`/entregas/checklist-preentrega/${itemId}/adjuntos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, adjuntos: resp.data.adjuntos } : i));
      toast.success('Archivo adjuntado');
      if (auditOpen) loadAudit();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Error subiendo archivo');
    } finally {
      setUploading(prev => ({ ...prev, [itemId]: false }));
      if (fileInputRefs.current[itemId]) fileInputRefs.current[itemId].value = '';
    }
  };

  const handleDeleteAdjunto = async (itemId, url) => {
    try {
      const resp = await api.delete(`/entregas/checklist-preentrega/${itemId}/adjuntos`, {
        params: { adjunto_url: url }
      });
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, adjuntos: resp.data.adjuntos } : i));
      toast.success('Adjunto eliminado');
      if (auditOpen) loadAudit();
    } catch (err) {
      toast.error('Error eliminando adjunto');
    }
  };

  // Stats
  const handleGenerar = async () => {
    setGenerando(true);
    try {
      const resp = await api.post(`/entregas/${entregaId}/checklist-preentrega/generar`);
      toast.success(`Checklist generado (${resp.data.creados} ítems)`);
      await loadItems();
      if (onGenerado) onGenerado();
    } catch (err) {
      toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Error generando checklist');
    } finally {
      setGenerando(false);
    }
  };

  const total = items.length;
  const completados = items.filter(i => i.es_conforme).length;
  const pctg = total > 0 ? Math.round((completados / total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-[#4E7A79]" size={24} />
        <span className="ml-2 text-sm text-[#888]">Cargando checklist...</span>
      </div>
    );
  }

  if (items.length === 0) {
    const puedeGenerar = tieneArquitecto && currentUser?.rol_nombre === 'Administrador';
    return (
      <div className="text-center py-12 text-[#888]" data-testid="checklist-vacio">
        <FileText size={36} className="mx-auto text-[#ccc] mb-3" />
        {tieneArquitecto ? (
          <>
            <p className="text-sm text-amber-700" data-testid="checklist-sin-generar">Esta entrega no tiene checklist generado</p>
            {puedeGenerar && (
              <Button size="sm" onClick={handleGenerar} disabled={generando} data-testid="btn-generar-checklist"
                className="mt-3 bg-[#4E7A79] hover:bg-[#3a5e5d] text-white">
                {generando ? <Loader2 size={14} className="mr-1 animate-spin" /> : <FileText size={14} className="mr-1" />}
                Generar checklist
              </Button>
            )}
          </>
        ) : (
          <p className="text-sm" data-testid="checklist-sin-arquitecto">El checklist se generará al asignar el arquitecto</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="checklist-preentrega">
      {/* Progress bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#2D2D2D]">Progreso</span>
          <Badge variant="outline" className="text-xs">{completados} / {total}</Badge>
        </div>
        <span className="text-sm font-semibold text-[#4E7A79]">{pctg}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-[#4E7A79] h-2 rounded-full transition-all duration-500"
          style={{ width: `${pctg}%` }}
        />
      </div>

      {/* Readonly notice */}
      {!editable && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle size={16} className="text-amber-500 shrink-0" />
          <p className="text-sm text-slate-600">
            <span className="font-medium">Solo lectura</span> — El checklist solo es editable a partir de la fase de inspección
          </p>
        </div>
      )}

      {/* Items */}
      <div className="divide-y divide-[#f0f0f0] border border-[#E8E8E7] rounded-lg overflow-hidden">
        {items.map((item, idx) => {
          const isToggling = toggling[item.id];
          const isUploading = uploading[item.id];
          const adjuntos = item.adjuntos || [];
          const canEdit = canToggle(item);

          return (
            <div key={item.id} className={`p-4 ${item.es_conforme ? 'bg-green-50/40' : 'bg-white'}`}>
              <div className="flex items-start gap-3">
                {/* Toggle */}
                <div className="pt-0.5">
                  {isToggling ? (
                    <Loader2 size={18} className="animate-spin text-[#4E7A79]" />
                  ) : (
                    <Switch
                      checked={item.es_conforme}
                      onCheckedChange={() => handleToggle(item)}
                      disabled={!canEdit}
                      data-testid={`checklist-toggle-${item.id}`}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${item.es_conforme ? 'text-green-800 line-through' : 'text-[#2D2D2D]'}`}>
                      {idx + 1}. {item.nombre}
                    </span>
                    {item.obligatorio && <Badge variant="outline" className="text-[9px] border-red-300 text-red-500">Obligatorio</Badge>}
                    {item.responsable_nombre && (
                      <Badge variant="outline" className="text-[9px] border-[#4E7A79]/30 text-[#4E7A79]">
                        {item.responsable_nombre}
                      </Badge>
                    )}
                  </div>

                  {/* Metadata de confirmación */}
                  {item.es_conforme && (item.confirmado_por_nombre || item.confirmado_at) && (
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-green-700/70">
                      {item.confirmado_por_nombre && (
                        <span className="flex items-center gap-0.5">
                          <User size={10} /> {item.confirmado_por_nombre}
                        </span>
                      )}
                      {item.confirmado_at && (
                        <span className="flex items-center gap-0.5">
                          <Clock size={10} /> {fmtFecha(item.confirmado_at)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Adjuntos */}
                  {adjuntos.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {adjuntos.map((adj, ai) => (
                        <div key={ai} className="flex items-center gap-2 group">
                          <Paperclip size={12} className="text-[#999] shrink-0" />
                          <a href={adj.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-[#5B7FA6] hover:underline truncate max-w-[200px]">
                            {adj.nombre}
                          </a>
                          {adj.uploaded_by_nombre && (
                            <span className="text-[10px] text-[#aaa]">por {adj.uploaded_by_nombre}</span>
                          )}
                          {adj.uploaded_at && (
                            <span className="text-[10px] text-[#ccc]">{fmtFecha(adj.uploaded_at)}</span>
                          )}
                          {editable && (
                            <Button
                              variant="ghost" size="sm"
                              className="h-5 w-5 p-0 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDeleteAdjunto(item.id, adj.url)}
                            >
                              <Trash2 size={11} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload */}
                  {editable && adjuntos.length < 3 && (
                    <div className="mt-2">
                      <input
                        type="file"
                        ref={el => fileInputRefs.current[item.id] = el}
                        className="hidden"
                        onChange={(e) => handleUpload(item.id, e.target.files?.[0])}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      />
                      <Button
                        variant="ghost" size="sm"
                        className="text-xs text-[#888] hover:text-[#4E7A79] h-7 px-2"
                        onClick={() => fileInputRefs.current[item.id]?.click()}
                        disabled={isUploading}
                        data-testid={`checklist-upload-${item.id}`}
                      >
                        {isUploading
                          ? <><Loader2 size={12} className="animate-spin mr-1" /> Subiendo...</>
                          : <><Upload size={12} className="mr-1" /> Adjuntar archivo</>
                        }
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Audit log section */}
      <div className="border border-[#E8E8E7] rounded-lg overflow-hidden">
        <button
          onClick={() => setAuditOpen(prev => !prev)}
          className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFAFA] hover:bg-[#f4f4f4] transition-colors"
          data-testid="checklist-audit-toggle"
        >
          <span className="text-xs font-medium text-[#666] flex items-center gap-1.5">
            <Clock size={13} /> Log de auditoría
            {auditLogs.length > 0 && (
              <Badge variant="outline" className="text-[9px] ml-1">{auditLogs.length}</Badge>
            )}
          </span>
          {auditOpen ? <ChevronUp size={14} className="text-[#888]" /> : <ChevronDown size={14} className="text-[#888]" />}
        </button>

        {auditOpen && (
          <div className="max-h-[300px] overflow-auto">
            {auditLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={16} className="animate-spin text-[#4E7A79]" />
                <span className="ml-2 text-xs text-[#888]">Cargando...</span>
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-6 text-xs text-[#aaa]">
                No hay registros de auditoría
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="checklist-audit-table">
                <thead className="sticky top-0 bg-[#FAFAFA] z-10">
                  <tr className="border-b border-[#E8E8E7]">
                    <th className="text-left px-4 py-2 font-medium text-[#888]">Fecha</th>
                    <th className="text-left px-4 py-2 font-medium text-[#888]">Actividad</th>
                    <th className="text-left px-4 py-2 font-medium text-[#888]">Acción</th>
                    <th className="text-left px-4 py-2 font-medium text-[#888]">Usuario</th>
                    <th className="text-left px-4 py-2 font-medium text-[#888]">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-b border-[#f0f0f0] hover:bg-[#FAFAFA]">
                      <td className="px-4 py-2 text-[#666] whitespace-nowrap">{fmtFecha(log.created_at)}</td>
                      <td className="px-4 py-2 text-[#333] max-w-[180px] truncate">{log.item_nombre || '-'}</td>
                      <td className={`px-4 py-2 font-medium ${ACCION_COLORS[log.accion] || 'text-[#666]'}`}>
                        {ACCION_LABELS[log.accion] || log.accion}
                      </td>
                      <td className="px-4 py-2 text-[#555]">{log.usuario_nombre || '-'}</td>
                      <td className="px-4 py-2 text-[#888]">
                        {log.detalles?.archivo || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistPreEntrega;
